import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { CheckCircle2, Clock, XCircle, MinusCircle, ChevronDown, ChevronRight, Sparkles } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { useAuth, API } from "../App";
import { extractError } from "../lib/errors";
import { toast } from "sonner";

const LeafIcon = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/>
    <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>
  </svg>
);

const STATUS_META = {
  compliant:      { label: "Conforme",    icon: CheckCircle2, color: "text-emerald-500", badge: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" },
  partial:        { label: "Parcial",     icon: Clock,        color: "text-amber-500",   badge: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" },
  non_compliant:  { label: "No Conforme", icon: XCircle,      color: "text-red-500",     badge: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" },
  not_applicable: { label: "No Aplica",   icon: MinusCircle,  color: "text-slate-400",   badge: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" },
};

const CLAUSES_ORDER = ["4", "5", "6", "7", "8", "9", "10"];
const CLAUSE_LABELS = {
  "4": "Cláusula 4 — Contexto de la Organización",
  "5": "Cláusula 5 — Liderazgo",
  "6": "Cláusula 6 — Planificación",
  "7": "Cláusula 7 — Apoyo",
  "8": "Cláusula 8 — Operación",
  "9": "Cláusula 9 — Evaluación del Desempeño",
  "10": "Cláusula 10 — Mejora",
};

const ControlRow = ({ control, response, onSave, onSuggest }) => {
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState(response?.status || "");
  const [notes, setNotes] = useState(response?.notes || "");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(control.id, { status, notes });
    setSaving(false);
  };

  const handleSuggest = async () => {
    setAiLoading(true);
    const text = await onSuggest(control);
    if (text) setNotes((prev) => (prev ? `${prev}\n\n${text}` : text));
    setAiLoading(false);
  };

  const meta = STATUS_META[status];

  return (
    <div className="border border-border rounded-lg">
      <button
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-muted/50 rounded-lg transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium truncate">{control.title}</span>
            {meta && (
              <Badge variant="outline" className={`text-[10px] ${meta.badge} flex-shrink-0`}>
                <meta.icon className="w-2.5 h-2.5 mr-1" />
                {meta.label}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{control.description}</p>
        </div>
        {expanded ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
      </button>
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
          <p className="text-sm text-muted-foreground">{control.description}</p>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-8 text-sm w-56">
              <SelectValue placeholder="Estado..." />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_META).map(([val, m]) => (
                <SelectItem key={val} value={val}>
                  <div className="flex items-center gap-2">
                    <m.icon className={`w-3.5 h-3.5 ${m.color}`} />
                    {m.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            placeholder="Notas de auditoría o referencia de evidencia..."
            className="text-sm h-20 resize-none"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={!status || saving}>
              {saving ? "Guardando..." : "Guardar"}
            </Button>
            <Button size="sm" variant="outline" onClick={handleSuggest} disabled={aiLoading}>
              <Sparkles className="w-3.5 h-3.5 mr-1.5" />
              {aiLoading ? "Generando..." : "Sugerir con AI"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const ZonaFrancaISO22000 = () => {
  const { token } = useAuth();
  const [controls, setControls] = useState([]);
  const [assessment, setAssessment] = useState({});
  const [loading, setLoading] = useState(true);
  const [openClauses, setOpenClauses] = useState({ "4": true, "5": true });

  const load = useCallback(async () => {
    try {
      const [ctrlRes, assRes] = await Promise.all([
        axios.get(`${API}/zona-franca/controls/iso22000`),
        axios.get(`${API}/zona-franca/assessment/iso22000`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setControls(ctrlRes.data.controls || []);
      setAssessment(assRes.data.responses || {});
    } catch {
      toast.error("No se pudo cargar la evaluación ISO 22000");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (controlId, body) => {
    try {
      const res = await axios.put(`${API}/zona-franca/assessment/iso22000/${controlId}`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssessment((prev) => ({ ...prev, [controlId]: body }));
      toast.success(`Control actualizado — ${res.data.completion_pct}%`);
    } catch {
      toast.error("Error al guardar");
    }
  };

  const handleSuggest = async (control) => {
    try {
      const res = await axios.post(
        `${API}/zona-franca/ai/suggest`,
        {
          framework_code: "iso22000",
          control_id: control.id,
          control_title: control.title,
          control_description: control.description,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Sugerencia generada");
      return res.data.suggestion;
    } catch (err) {
      toast.error(extractError(err, "No se pudo generar sugerencia"));
      return "";
    }
  };

  const byClause = CLAUSES_ORDER.reduce((acc, cl) => {
    acc[cl] = controls.filter((c) => c.clause === cl);
    return acc;
  }, {});

  const answered = Object.keys(assessment).length;
  const total = controls.length;
  const compliant = Object.values(assessment).filter((r) => r.status === "compliant").length;
  const completionPct = total ? Math.round((answered / total) * 100) : 0;
  const compliancePct = total ? Math.round((compliant / total) * 100) : 0;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "#3b82f620", border: "1px solid #3b82f640" }}>
            <LeafIcon className="w-5 h-5 text-blue-500" />
          </div>
          <div>
            <h1 className="text-xl font-heading font-bold">ISO 22000:2018</h1>
            <p className="text-sm text-muted-foreground">Sistema de Gestión de Inocuidad Alimentaria — Cláusulas 4–10</p>
          </div>
        </div>
        <Badge variant="outline" className="text-xs">ISO / INDOCAL</Badge>
      </div>

      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Evaluación", value: `${completionPct}%` },
          { label: "Conformidad", value: `${compliancePct}%` },
          { label: "Conformes", value: compliant },
        ].map((k) => (
          <Card key={k.label}><CardContent className="p-4">
            <p className="text-2xl font-bold text-blue-500">{loading ? "—" : k.value}</p>
            <p className="text-xs text-muted-foreground">{k.label}</p>
          </CardContent></Card>
        ))}
      </div>

      <Card><CardContent className="p-4">
        <div className="flex justify-between text-sm mb-1">
          <span>Progreso ISO 22000</span>
          <span className="font-medium">{completionPct}%</span>
        </div>
        <Progress value={completionPct} className="h-2" />
      </CardContent></Card>

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : (
        <div className="space-y-4">
          {CLAUSES_ORDER.map((cl) => {
            const items = byClause[cl] || [];
            const clAnswered = items.filter((c) => assessment[c.id]?.status).length;
            const clCompliant = items.filter((c) => assessment[c.id]?.status === "compliant").length;
            const isOpen = openClauses[cl] ?? false;

            return (
              <Card key={cl}>
                <CardHeader className="pb-0">
                  <button
                    className="w-full flex items-center justify-between text-left"
                    onClick={() => setOpenClauses((p) => ({ ...p, [cl]: !p[cl] }))}
                  >
                    <div className="flex items-center gap-3">
                      <LeafIcon className="w-4 h-4 text-blue-500" />
                      <div>
                        <CardTitle className="text-sm">{CLAUSE_LABELS[cl]}</CardTitle>
                        <CardDescription className="text-xs">
                          {clCompliant} conformes · {clAnswered}/{items.length} evaluados
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Progress value={items.length ? Math.round((clAnswered / items.length) * 100) : 0} className="h-1.5 w-24" />
                      {isOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                    </div>
                  </button>
                </CardHeader>
                {isOpen && (
                  <CardContent className="pt-3 space-y-2">
                    {items.map((ctrl) => (
                      <ControlRow key={ctrl.id} control={ctrl} response={assessment[ctrl.id]} onSave={handleSave} onSuggest={handleSuggest} />
                    ))}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ZonaFrancaISO22000;
